import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import { OwnerPageInfo } from "burde";
import { useResources } from "contexts/resource-context";
import { getCalendarType, getOwnerPage } from "page/calendar/store";
import { ApplicationState } from "store";
import { actionCreators as uiActionCreators } from "ui/store";
import * as styles from "./owner-page.css";
import { actionCreators as calendarActionCreators } from "page/calendar/store";
import { Checkbox } from "util/components/checkbox";
import { CalendarType } from "svg/constants";

type OwnerInformationFormProps = {
    ownerPage: OwnerPageInfo;
    calendarType: CalendarType;
} & typeof calendarActionCreators &
    typeof uiActionCreators;

const OwnerInformationForm: React.FC<OwnerInformationFormProps> = ({
    ownerPage,
    calendarType,
    updateOwnerPage,
}) => {
    const [ownerPageInfo, setOwnerPageInfo] = useState(ownerPage);
    const [ownerPageUpdatedFromProps, setOwnerPageUpdatedFromProps] = useState(
        false,
    );
    const [isGift, setIsGift] = useState(false);
    const isInitialMount = useRef(true);
    const {
        Owner_Page_Form_Email,
        Owner_Page_Form_Name,
        Owner_Page_Form_PhoneNumber,
        Owner_Page_Form_IsGift,
        Owner_Page_Form_Greeting,
        Owner_Page_Form_Notebook_Name,
    } = useResources();

    // The owner page can be set from temporary calendar code in calendar.tsx.
    useEffect(() => {
        if (!_.isEqual(ownerPage, ownerPageInfo)) {
            setOwnerPageInfo(ownerPage);
            setIsGift(ownerPage.isGift);
            setOwnerPageUpdatedFromProps(true);
        }
    }, [ownerPage]);

    useEffect(() => {
        if (!ownerPageUpdatedFromProps) {
            updateOwnerPage({
                ...ownerPage,
                email: ownerPageInfo.email,
                name: ownerPageInfo.name,
                phoneNumber: ownerPageInfo.phoneNumber,
                isGift: ownerPageInfo.isGift,
                greeting: ownerPageInfo.greeting,
            });
        } else {
            setOwnerPageUpdatedFromProps(false);
        }
    }, [ownerPageInfo]);

    useEffect(() => {
        if (isInitialMount.current) {
            setIsGift(ownerPageInfo.isGift);
            isInitialMount.current = false;
        } else {
            setOwnerPageInfo({
                ...ownerPage,
                isGift,
                greeting: isGift ? ownerPageInfo.greeting : "",
            });
        }
    }, [isGift]);

    return (
        <div style={{ paddingBottom: "20px" }}>
            <label>
                {calendarType === CalendarType.Calendar
                    ? `${Owner_Page_Form_Name}`
                    : `${Owner_Page_Form_Notebook_Name}`}
            </label>
            <input
                type="text"
                className={styles.inputFormInput}
                value={ownerPageInfo.name || ""}
                maxLength={32}
                onChange={(e) =>
                    setOwnerPageInfo({
                        ...ownerPageInfo,
                        name: e.target.value,
                    })
                }
            />
            <label>{Owner_Page_Form_Email}</label>
            <input
                type="text"
                className={styles.inputFormInput}
                value={ownerPageInfo.email || ""}
                maxLength={32}
                onChange={(e) =>
                    setOwnerPageInfo({
                        ...ownerPageInfo,
                        email: e.target.value,
                    })
                }
            />
            <label>{Owner_Page_Form_PhoneNumber}</label>
            <input
                type="text"
                className={styles.inputFormInput}
                value={ownerPageInfo.phoneNumber || ""}
                maxLength={32}
                onChange={(e) =>
                    setOwnerPageInfo({
                        ...ownerPageInfo,
                        phoneNumber: e.target.value,
                    })
                }
            />
            <div className={styles.isGiftWrapper}>
                <span>{Owner_Page_Form_IsGift}</span>
                <div style={{ height: "20px" }}>
                    <Checkbox
                        onClick={() => setIsGift(!isGift)}
                        checked={ownerPageInfo.isGift}
                    />
                </div>
            </div>
            <label
                className={[
                    ownerPageInfo.isGift ? "" : styles.inputFormHidden,
                    styles.ownerPageGiftLabel,
                ].join(" ")}>
                {Owner_Page_Form_Greeting}
            </label>
            <input
                className={
                    ownerPageInfo.isGift
                        ? styles.inputFormInput
                        : styles.inputFormHidden
                }
                type="text"
                disabled={!ownerPageInfo.isGift}
                value={ownerPageInfo.greeting || ""}
                maxLength={32}
                onChange={(e) =>
                    setOwnerPageInfo({
                        ...ownerPageInfo,
                        greeting: e.target.value,
                    })
                }
            />
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    ownerPage: getOwnerPage(state),
    calendarType: getCalendarType(state),
});

const mapDispatchToProps = {
    ...calendarActionCreators,
    ...uiActionCreators,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(OwnerInformationForm);
